import { CsvItineraryRow, PortOfCall, ValidationError } from '../types/itinerary';

/**
 * Parses a CSV file containing cruise itinerary data
 * Expected CSV structure:
 * - Headers: cruiseLine, shipName, sailingDate, returnDate, departurePort, itineraryTitle, cruiseLength, shipLocation, shipUrl, portsOfCall (JSON array)
 * - portsOfCall should be a JSON stringified array of {name, date, arrivalTime?, departureTime?}
 */
export function parseItineraryCsv(csvContent: string): {
  data: CsvItineraryRow[];
  errors: ValidationError[];
  totalFileRows: number;
  errorRowNumbers: Set<number>; // Track which row numbers had errors
} {
  const errors: ValidationError[] = [];
  const data: CsvItineraryRow[] = [];
  const errorRowNumbers = new Set<number>();
  
  const lines = csvContent.trim().split('\n');
  const totalFileRows = lines.length;
  
  if (lines.length < 2) {
    errors.push({
      row: 0,
      message: 'CSV file must contain at least a header row and one data row'
    });
    return { data, errors, totalFileRows, errorRowNumbers };
  }
  
  // Parse header row
  const headerRow = parseCsvRow(lines[0]);
  const requiredHeaders = ['cruiseLine', 'shipName', 'sailingDate', 'returnDate', 'departurePort'];
  const missingHeaders = requiredHeaders.filter(h => !headerRow.includes(h));
  
  if (missingHeaders.length > 0) {
    errors.push({
      row: 1,
      message: `Missing required headers: ${missingHeaders.join(', ')}`
    });
    return { data, errors, totalFileRows, errorRowNumbers };
  }
  
  // Create header index map
  const headerIndex: { [key: string]: number } = {};
  headerRow.forEach((header, index) => {
    headerIndex[header.trim()] = index;
  });
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines
    
    try {
      const values = parseCsvRow(line);
      const rowNumber = i + 1; // 1-indexed for user display
      
      // Extract required fields
      const cruiseLine = getValue(values, headerIndex, 'cruiseLine', rowNumber, errors);
      const shipName = getValue(values, headerIndex, 'shipName', rowNumber, errors);
      const sailingDate = getValue(values, headerIndex, 'sailingDate', rowNumber, errors);
      const returnDate = getValue(values, headerIndex, 'returnDate', rowNumber, errors);
      const departurePort = getValue(values, headerIndex, 'departurePort', rowNumber, errors);
      
      // Validate required fields
      if (!cruiseLine || !shipName || !sailingDate || !returnDate || !departurePort) {
        errorRowNumbers.add(rowNumber);
        continue; // Skip this row, error already added
      }
      
      // Validate and convert date formats
      const sailingDateValid = validateDate(sailingDate, rowNumber, 'sailingDate', errors);
      const returnDateValid = validateDate(returnDate, rowNumber, 'returnDate', errors);
      
      if (!sailingDateValid || !returnDateValid) {
        errorRowNumbers.add(rowNumber);
        continue; // Skip this row
      }
      
      // Validate return date is after sailing date
      if (new Date(returnDateValid) < new Date(sailingDateValid)) {
        errors.push({
          row: rowNumber,
          field: 'returnDate',
          message: `Return date must be after sailing date`
        });
        errorRowNumbers.add(rowNumber);
        continue;
      }
      
      // Parse optional fields
      const itineraryTitle = getValue(values, headerIndex, 'itineraryTitle', rowNumber, errors, true) || '';
      const cruiseLengthStr = getValue(values, headerIndex, 'cruiseLength', rowNumber, errors, true) || '0';
      const cruiseLength = parseInt(cruiseLengthStr, 10);
      
      if (isNaN(cruiseLength)) {
        errors.push({
          row: rowNumber,
          field: 'cruiseLength',
          message: `Invalid cruise length: ${cruiseLengthStr}`
        });
        errorRowNumbers.add(rowNumber);
      }
      
      // Parse portsOfCall - support both JSON array and column-based format
      let portsOfCall: PortOfCall[] = [];
      
      // First, try JSON format (portsOfCall column)
      const portsOfCallStr = getValue(values, headerIndex, 'portsOfCall', rowNumber, errors, true);
      if (portsOfCallStr && portsOfCallStr.trim()) {
        try {
          const parsed = JSON.parse(portsOfCallStr);
          if (Array.isArray(parsed)) {
            portsOfCall = parsed.map((port: any) => {
              // Convert port date format if needed
              const portDate = port.date ? convertDateFormat(port.date) || port.date : '';
              return {
                name: port.name || '',
                date: portDate,
                arrivalTime: port.arrivalTime,
                departureTime: port.departureTime
              };
            });
          } else {
            errors.push({
              row: rowNumber,
              field: 'portsOfCall',
              message: 'portsOfCall must be a JSON array'
            });
            errorRowNumbers.add(rowNumber);
          }
        } catch {
          // If JSON parsing fails, try column-based format
          console.log(`[ItineraryParser] JSON parse failed for portsOfCall, trying column-based format`);
        }
      }
      
      // If no ports from JSON, try column-based format (portsOfCall/0/portName, etc.)
      if (portsOfCall.length === 0) {
        const portIndexes = new Set<number>();
        
        // Find all port indexes from column headers (case-insensitive)
        Object.keys(headerIndex).forEach(header => {
          const match = header.match(/^portsOfCall\/(\d+)\/portName$/i);
          if (match) {
            portIndexes.add(parseInt(match[1], 10));
          }
        });
        
        console.log(`[ItineraryParser] Found ${portIndexes.size} port indexes:`, Array.from(portIndexes).sort((a, b) => a - b));
        
        // Parse each port from columns (case-insensitive header lookup)
        Array.from(portIndexes).sort((a, b) => a - b).forEach(index => {
          // Find the actual header key (case-insensitive)
          const portNameKey = Object.keys(headerIndex).find(h => 
            h.match(new RegExp(`^portsOfCall/${index}/portName$`, 'i'))
          );
          const portDateKey = Object.keys(headerIndex).find(h => 
            h.match(new RegExp(`^portsOfCall/${index}/portDate$`, 'i'))
          );
          const arrivalTimeKey = Object.keys(headerIndex).find(h => 
            h.match(new RegExp(`^portsOfCall/${index}/arrivalTime$`, 'i'))
          );
          const departureTimeKey = Object.keys(headerIndex).find(h => 
            h.match(new RegExp(`^portsOfCall/${index}/departureTime$`, 'i'))
          );
          
          const portName = portNameKey ? getValue(values, headerIndex, portNameKey, rowNumber, errors, true) : undefined;
          const portDate = portDateKey ? getValue(values, headerIndex, portDateKey, rowNumber, errors, true) : undefined;
          const arrivalTime = arrivalTimeKey ? getValue(values, headerIndex, arrivalTimeKey, rowNumber, errors, true) : undefined;
          const departureTime = departureTimeKey ? getValue(values, headerIndex, departureTimeKey, rowNumber, errors, true) : undefined;
          
          console.log(`[ItineraryParser] Port ${index}: name="${portName}", date="${portDate}", arrival="${arrivalTime}", departure="${departureTime}"`);
          
          // Only add port if it has a name and date
          if (portName && portDate) {
            const convertedDate = convertDateFormat(portDate) || portDate;
            portsOfCall.push({
              name: portName.trim(),
              date: convertedDate,
              arrivalTime: arrivalTime?.trim() || undefined,
              departureTime: departureTime?.trim() || undefined
            });
            console.log(`[ItineraryParser] Added port: ${portName.trim()} on ${convertedDate}`);
          }
        });
        
        console.log(`[ItineraryParser] Total ports parsed: ${portsOfCall.length}`);
      }
      
      // Parse ship metadata (optional)
      const shipLocation = getValue(values, headerIndex, 'shipLocation', rowNumber, errors, true);
      const shipUrl = getValue(values, headerIndex, 'shipUrl', rowNumber, errors, true);
      const shipDeckplan = getValue(values, headerIndex, 'shipDeckplan', rowNumber, errors, true);
      
      data.push({
        cruiseLine: cruiseLine.trim(),
        shipName: shipName.trim(),
        sailingDate: sailingDateValid, // Use converted date
        returnDate: returnDateValid, // Use converted date
        departurePort: departurePort.trim(),
        portsOfCall,
        itineraryTitle: itineraryTitle.trim(),
        cruiseLength: isNaN(cruiseLength) ? 0 : cruiseLength,
        shipLocation: shipLocation?.trim(),
        shipUrl: shipUrl?.trim(),
        shipDeckplan: shipDeckplan?.trim()
      });
    } catch (error) {
      const rowNumber = i + 1;
      errors.push({
        row: rowNumber,
        message: `Error parsing row: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      errorRowNumbers.add(rowNumber);
    }
  }
  
  return { data, errors, totalFileRows, errorRowNumbers };
}

/**
 * Helper function to parse a CSV row, handling quoted values
 */
function parseCsvRow(row: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    const nextChar = row[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of value
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last value
  values.push(current);
  
  return values;
}

/**
 * Helper function to get a value from the CSV row
 */
function getValue(
  values: string[],
  headerIndex: { [key: string]: number },
  headerName: string,
  rowNumber: number,
  errors: ValidationError[],
  optional: boolean = false
): string | undefined {
  const index = headerIndex[headerName];
  
  if (index === undefined) {
    if (!optional) {
      errors.push({
        row: rowNumber,
        field: headerName,
        message: `Missing required field: ${headerName}`
      });
    }
    return undefined;
  }
  
  const value = values[index]?.trim();
  
  if (!value && !optional) {
    errors.push({
      row: rowNumber,
      field: headerName,
      message: `Empty required field: ${headerName}`
    });
    return undefined;
  }
  
  return value;
}

/**
 * Convert date from MM/DD/YYYY or M/D/YYYY to YYYY-MM-DD format
 */
function convertDateFormat(dateStr: string): string | null {
  if (!dateStr || !dateStr.trim()) {
    return null;
  }
  
  const trimmed = dateStr.trim();
  
  // Check if already in YYYY-MM-DD format
  const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (isoRegex.test(trimmed)) {
    return trimmed;
  }
  
  // Try MM/DD/YYYY or M/D/YYYY format
  const usDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = trimmed.match(usDateRegex);
  if (match) {
    const month = match[1].padStart(2, '0');
    const day = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  }
  
  return null;
}

/**
 * Validate date format (accepts YYYY-MM-DD or MM/DD/YYYY) and convert to YYYY-MM-DD
 */
function validateDate(
  dateStr: string,
  rowNumber: number,
  fieldName: string,
  errors: ValidationError[]
): string | null {
  if (!dateStr) {
    return null;
  }
  
  // Convert date format
  const convertedDate = convertDateFormat(dateStr);
  if (!convertedDate) {
    errors.push({
      row: rowNumber,
      field: fieldName,
      message: `Invalid date format: ${dateStr}. Expected YYYY-MM-DD or MM/DD/YYYY`
    });
    return null;
  }
  
  // Check if it's a valid date
  const date = new Date(convertedDate);
  if (isNaN(date.getTime())) {
    errors.push({
      row: rowNumber,
      field: fieldName,
      message: `Invalid date: ${dateStr}`
    });
    return null;
  }
  
  return convertedDate;
}

